import React, { useEffect, useRef, useState } from 'react';
import { getNaplanHref } from '@/config/naplanStates';
import { Link } from 'react-router-dom';
import {
  GraduationCap,
  ClipboardCheck,
  Target,
  Trophy,
  Brain,
  PenTool,
  Calculator,
  ChevronRight,
  ArrowLeft,
  MapPin,
  BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation as useLocationContext } from '@/contexts/LocationContext';
import { COURSE_FAMILY_STATES } from '@/config/courseStateMapping';
import type { AustralianState } from '@/data/courses/courses';

export type MegaPanel = 'courses' | 'centres' | null;

interface MegaMenuProps {
  panel: MegaPanel;
  onClose: () => void;
}

type CourseKey = 'foundations' | 'naplan' | 'oc' | 'selective' | 'sec' | 'gate';

interface CourseSidebarItem {
  key: CourseKey;
  label: string;
  states: AustralianState[];
}

const ALL_COURSE_ITEMS: CourseSidebarItem[] = [
  { key: 'foundations', label: 'Foundations', states: ['NSW', 'VIC', 'WA'] },
  { key: 'naplan', label: 'NAPLAN Preparation', states: COURSE_FAMILY_STATES.naplan },
  { key: 'oc', label: 'OC Preparation', states: COURSE_FAMILY_STATES.oc },
  {
    key: 'selective',
    label: 'Selective Entry',
    states: [...COURSE_FAMILY_STATES.selective_nsw, ...COURSE_FAMILY_STATES.selective_vic],
  },
  { key: 'sec', label: 'SEC Scholarship', states: COURSE_FAMILY_STATES.scholarship_vic },
  { key: 'gate', label: 'ASET / GATE Prep', states: COURSE_FAMILY_STATES.gate },
];

const COURSE_ICONS: Record<CourseKey, React.ComponentType<{ className?: string }>> = {
  foundations: GraduationCap,
  naplan: ClipboardCheck,
  oc: Target,
  selective: Trophy,
  sec: Target,
  gate: Trophy,
};


const MegaMenu: React.FC<MegaMenuProps> = ({ panel, onClose }) => {
  const {
    selectedState,
    currentStateConfig,
    stateConfigs,
    setSelectedState,
    selectedCenter,
    setSelectedCenter,
    setSelectedCenterId,
  } = useLocationContext();

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Courses panel internal state
  const [activeCourse, setActiveCourse] = useState<CourseKey>('foundations');
  const [step, setStep] = useState<1 | 2>(1);
  const [chosenYear, setChosenYear] = useState<string | null>(null);


  // Centres panel state
  const [centresState, setCentresState] = useState<string>(selectedState);

  // Reset when panel changes
  useEffect(() => {
    if (panel === 'courses') {
      setActiveCourse('foundations');
      setStep(1);
      setChosenYear(null);
    }

    if (panel === 'centres') {
      setCentresState(selectedState);
    }
  }, [panel, selectedState]);

  // Esc + outside click
  useEffect(() => {
    if (!panel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (containerRef.current && !containerRef.current.contains(t)) {
        // ignore if clicking the nav triggers themselves
        const trigger = (t as HTMLElement).closest?.('[data-megamenu-trigger]');
        if (!trigger) onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [panel, onClose]);

  if (!panel) return null;

  const stateCode = (selectedState === 'VIC' ? 'VIC' : selectedState === 'WA' ? 'WA' : 'NSW') as 'NSW' | 'VIC' | 'WA';
  const courseItems = ALL_COURSE_ITEMS.filter((c) => c.states.includes(stateCode));

  const yearLevels =
    stateCode === 'VIC'
      ? [
          { label: 'Prep', slug: 'prep' },
          { label: 'Year 1', slug: 'year-1' },
          { label: 'Year 2', slug: 'year-2' },
          { label: 'Year 3', slug: 'year-3' },
          { label: 'Year 4', slug: 'year-4' },
          { label: 'Year 5', slug: 'year-5' },
          { label: 'Year 6', slug: 'year-6' },
          { label: 'Year 7', slug: 'year-7' },
          { label: 'Year 8', slug: 'year-8' },
        ]
      : [
          { label: stateCode === 'WA' ? 'Pre-primary' : 'Kindergarten', slug: 'kindergarten' },
          { label: 'Year 1', slug: 'year-1' },
          { label: 'Year 2', slug: 'year-2' },
          { label: 'Year 3', slug: 'year-3' },
          { label: 'Year 4', slug: 'year-4' },
          { label: 'Year 5', slug: 'year-5' },
          { label: 'Year 6', slug: 'year-6' },
          { label: 'Year 7', slug: 'year-7' },
          { label: 'Year 8', slug: 'year-8' },
        ];


  const foundationsBlurb =
    stateCode === 'VIC'
      ? 'English and Maths programmes aligned to the Victorian Curriculum.'
      : 'English and Maths programmes aligned to the NSW (NESA) Curriculum.';

  const curriculumBadge =
    stateCode === 'VIC' ? 'Victorian Curriculum aligned' : 'NESA aligned';

  const handleYearPick = (slug: string) => {
    setChosenYear(slug);
    setTimeout(() => setStep(2), 150);
  };

  const buildFoundationsHref = (year: string, subject: 'english' | 'maths') => {
    const base = stateCode === 'WA' && year === 'kindergarten' ? 'pre-primary' : year;
    const yearSlug = `${base}-foundations`;
    return `/programs/${stateCode.toLowerCase()}/${yearSlug}-${subject}-tutoring`;
  };

  const yearLabel = (slug: string | null) =>
    yearLevels.find((y) => y.slug === slug)?.label ?? '';

  // Static detail content for non-foundations courses
  const courseDetails: Record<
    Exclude<CourseKey, 'foundations'>,
    {
      title: string;
      description: string;
      years: string;
      learnHref: string;
      booksHref: string;
    }
  > = {
    naplan: {
      title: 'NAPLAN Preparation',
      description:
        'Short-cycle programme aligned to the NAPLAN test window - reading, writing, numeracy and test technique.',
      years: 'Years 3, 5 and 7',
      learnHref: getNaplanHref(stateCode),
      booksHref: '/books-materials',
    },
    oc: {
      title: 'OC Preparation',
      description:
        'Targeted preparation for the NSW Opportunity Class placement test - thinking skills, reading and mathematical reasoning.',
      years: 'Year 3 - Year 4',
      learnHref: '/programs/oc-preparation',
      booksHref: '/books-materials',
    },
    selective: {
      title: stateCode === 'VIC' ? 'Selective Entry (VSEC)' : 'Selective TMRW™',
      description:
        stateCode === 'VIC'
          ? 'Preparation for the Victorian Selective Entry High School examination.'
          : 'Intensive preparation for NSW Selective High School entrance with the TMRW™ method.',
      years: stateCode === 'VIC' ? 'Year 5 - Year 8' : 'Year 5 - Year 6',
      learnHref: stateCode === 'VIC' ? '/programs/vsec' : '/programs/selective-school',
      booksHref: '/books-materials',
    },

    sec: {
      title: 'SEC Scholarship Exam',
      description:
        'Expert preparation for Victorian scholarship exams covering humanities, mathematics, reasoning and written expression.',
      years: 'Year 5 - Year 8',
      learnHref: '/programs/scholarship-exam-course',
      booksHref: '/books-materials',
    },
    gate: {
      title: 'ASET / GATE Test Preparation',
      description:
        'Preparation for the WA Academic Selective Entrance Test (ASET) and Gifted and Talented Education (GATE) selection process - Reading Comprehension, Communicating Ideas in Writing, Quantitative Reasoning and Abstract Reasoning.',
      years: 'Year 5 - Year 6',
      learnHref: '/courses/aset-gate-test-preparation',
      booksHref: '/courses/aset-gate-test-pack',
    },
  };

  // ----- Centres panel data -----
  const centresStateConfig = stateConfigs.find((s) => s.code === centresState);
  const centresList = centresStateConfig?.centers ?? [];
  const supportedCentreStates = stateConfigs.filter((s) => s.centers.length > 0);

  const bookHref = (extra: Record<string, string> = {}) => {
    const params = new URLSearchParams(extra);
    const qs = params.toString();
    return qs ? `/book-assessment?${qs}` : '/book-assessment';
  };

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label={panel === 'courses' ? 'Courses menu' : 'Find a Centre menu'}
      className="absolute left-0 right-0 top-full z-[9998] border-b border-border bg-background shadow-2xl animate-in fade-in-0 slide-in-from-top-2 duration-150"
    >
      <div className="container mx-auto flex min-h-[460px]">
        {/* SIDEBAR */}
        <aside className="w-[200px] flex-shrink-0 border-r border-border bg-[hsl(var(--background))] py-6">
          {panel === 'courses' ? (
            <nav aria-label="Course categories">
              {courseItems.map((c) => {
                const Icon = COURSE_ICONS[c.key];
                const active = activeCourse === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => {
                      setActiveCourse(c.key);
                      setStep(1);
                      setChosenYear(null);
                    }}

                    className={`group flex w-full items-center gap-3 px-5 py-3 text-sm font-medium transition-colors duration-150 border-l-[3px] ${
                      active
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-transparent text-foreground/80 hover:bg-primary/5 hover:text-primary'
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-left">{c.label}</span>
                  </button>
                );
              })}
            </nav>
          ) : (
            <>
              <nav aria-label="States">
                {supportedCentreStates.map((s) => {
                  const active = centresState === s.code;
                  return (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => setCentresState(s.code)}
                      className={`flex w-full items-center gap-3 px-5 py-3 text-sm font-medium transition-colors duration-150 border-l-[3px] ${
                        active
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-transparent text-foreground/80 hover:bg-primary/5 hover:text-primary'
                      }`}
                    >
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      {s.name}
                    </button>
                  );
                })}
              </nav>
              <div className="mt-6 px-4">
                <div className="rounded-lg border border-border bg-card p-4">
                  <p className="text-xs font-medium text-foreground mb-2">
                    Interested in opening a Schoolola franchise?
                  </p>
                  <Link to="/franchise" onClick={onClose}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-center text-primary hover:bg-primary/5"
                    >
                      Learn about franchising
                    </Button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </aside>

        {/* CONTENT */}
        <section className="flex-1 px-10 py-8">
          {panel === 'courses' && activeCourse === 'foundations' && (
            <div>
              {/* Breadcrumb for step 2 */}
              {step > 1 && (
                <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-5">
                  <button
                    onClick={() => {
                      setStep(1);
                      setChosenYear(null);
                    }}
                    className="hover:text-primary transition-colors"
                  >
                    Foundations
                  </button>
                  <ChevronRight className="h-3.5 w-3.5" />
                  <span className="text-foreground">{yearLabel(chosenYear)}</span>
                </nav>
              )}


              {step === 1 && (
                <>
                  <h2 className="text-2xl font-bold text-foreground mb-2">Foundations</h2>
                  <p className="text-sm text-muted-foreground mb-6">{foundationsBlurb}</p>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Select a year level
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {yearLevels.map((y) => {
                      const active = chosenYear === y.slug;
                      return (
                        <button
                          key={y.slug}
                          onClick={() => handleYearPick(y.slug)}
                          className={`rounded-full px-4 py-2 text-sm font-medium border transition-all duration-150 ${
                            active
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-foreground hover:border-primary hover:text-primary hover:bg-primary/5'
                          }`}
                        >
                          {y.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {step === 2 && chosenYear && (
                <>
                  <h2 className="text-2xl font-bold text-foreground mb-1">
                    Foundations - {yearLabel(chosenYear)}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">Choose a subject</p>
                  <div className="grid grid-cols-2 gap-4 max-w-2xl">
                    {(['english', 'maths'] as const).map((sub) => {
                      const Icon = sub === 'english' ? PenTool : Calculator;
                      return (
                        <Link
                          key={sub}
                          to={buildFoundationsHref(chosenYear, sub)}
                          onClick={onClose}
                          className="group rounded-xl border border-border bg-card p-5 text-left transition-all duration-150 hover:border-primary hover:bg-primary/5 hover:shadow-md"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className="rounded-lg bg-primary/10 p-2 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                              <Icon className="h-5 w-5" />
                            </div>
                            <span className="text-base font-semibold capitalize text-foreground">
                              {sub}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {sub === 'english'
                              ? 'Reading, writing, grammar and vocabulary.'
                              : 'Number, algebra, measurement and geometry.'}
                          </p>
                          <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
                            View course details
                            <ChevronRight className="h-3.5 w-3.5" />
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}

            </div>
          )}

          {panel === 'courses' && activeCourse !== 'foundations' && (() => {
            const d = courseDetails[activeCourse];
            const Icon = COURSE_ICONS[activeCourse];
            return (
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground">{d.title}</h2>
                </div>
                <p className="text-sm text-muted-foreground mb-5">{d.description}</p>
                <div className="mb-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Year levels
                  </p>
                  <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-medium">
                    {d.years}
                  </span>
                </div>
                {activeCourse !== 'naplan' && activeCourse !== 'sec' && (
                  <div className="mb-6">
                    <Link
                      to={d.booksHref}
                      onClick={onClose}
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                    >
                      <BookOpen className="h-4 w-4" />
                      Books &amp; Materials for {d.title}
                    </Link>
                  </div>
                )}
                <div className="flex flex-wrap gap-3">
                  <Link to={bookHref({ course: activeCourse })} onClick={onClose}>
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-5">
                      Book a free assessment
                    </Button>
                  </Link>
                  <Link to={d.learnHref} onClick={onClose}>
                    <Button
                      variant="ghost"
                      className="rounded-full text-primary hover:bg-primary/5 px-5"
                    >
                      Learn more
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })()}

          {panel === 'centres' && (() => {
            const activeCentre = currentStateConfig?.centers.find(
              (c) => c.slug === selectedCenter,
            );
            return (
            <div>
              {activeCentre && (
                <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2.5">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="text-muted-foreground">Showing prices for:</span>
                    <span className="font-semibold text-foreground">{activeCentre.suburb}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCenter('');
                      setSelectedCenterId(null);
                    }}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    Clear
                  </button>
                </div>
              )}
              <h2 className="text-2xl font-bold text-foreground mb-1">
                {centresStateConfig?.name ?? centresState} Centres
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {centresList.length > 0
                  ? `${centresList.length} centre${centresList.length === 1 ? '' : 's'} available`
                  : 'No centres listed yet for this state.'}
              </p>

              {centresList.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  {centresList.map((c) => {
                    const dim = c.comingSoon;
                    return (
                      <Link
                        key={c.id}
                        to={`/centres/${c.slug}`}
                        onClick={() => {
                          setSelectedState(c.stateCode);
                          onClose();
                        }}
                        className={`group rounded-lg border border-border bg-card p-4 transition-all duration-150 hover:border-primary hover:bg-primary/5 hover:shadow-md ${
                          dim ? 'opacity-65' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-sm font-semibold text-foreground group-hover:text-primary">
                            {c.name}
                          </span>
                          {dim ? (
                            <span className="inline-flex items-center rounded-full bg-accent/20 text-accent px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider">
                              Opening Soon
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider">
                              Open Now
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{c.suburb}</p>
                      </Link>
                    );
                  })}
                </div>
              )}
              <div className="mt-6">
                <Link
                  to={`/states/${(centresStateConfig?.name ?? centresState).toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={onClose}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  See all {centresStateConfig?.name ?? centresState} centres
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            );
          })()}

        </section>
      </div>
    </div>
  );
};

export default MegaMenu;
